import { meetingsApi } from './meetings-api';

type Job = {
  enabled: boolean;
  version: number;
  running: boolean;
  delay: number;
  timer?: ReturnType<typeof setTimeout>;
};
type Update = { meetingId: string; enabled: boolean; failed: boolean };
/** Serialize each meeting's writes so an older retry cannot undo a newer choice. */
export function createIdentificationWriter(
  send: (id: string, enabled: boolean) => Promise<unknown>,
  notify: (update: Update) => void,
  schedule = (run: () => void, ms: number) => setTimeout(run, ms),
  cancel = (timer: ReturnType<typeof setTimeout>) => clearTimeout(timer),
) {
  const jobs = new Map<string, Job>();
  async function run(id: string, job: Job) {
    if (job.running || jobs.get(id) !== job) return;
    job.running = true;
    const version = job.version;
    const enabled = job.enabled;
    let retry = false;
    try {
      await send(id, enabled);
      if (jobs.get(id) === job && version === job.version) {
        jobs.delete(id);
        notify({ meetingId: id, enabled, failed: false });
      }
    } catch (error) {
      if (jobs.get(id) !== job) return;
      const status = (error as { status?: number } | null)?.status;
      const permanent =
        status !== undefined && [400, 401, 403, 404, 409, 410, 422].includes(status);
      if (version === job.version) {
        notify({ meetingId: id, enabled, failed: true });
        if (permanent) jobs.delete(id);
        else retry = true;
      }
    } finally {
      job.running = false;
      if (jobs.get(id) === job) {
        if (version !== job.version) void run(id, job);
        else if (retry) {
          job.timer = schedule(() => void run(id, job), job.delay);
          job.delay = Math.min(job.delay * 2, 30_000);
        }
      }
    }
  }
  return {
    set(id: string, enabled: boolean) {
      const job = jobs.get(id) ?? { enabled, version: 0, running: false, delay: 250 };
      if (job.timer) cancel(job.timer);
      job.enabled = enabled;
      job.version++;
      job.delay = 250;
      jobs.set(id, job);
      void run(id, job);
    },
    clear() {
      for (const job of jobs.values()) if (job.timer) cancel(job.timer);
      jobs.clear();
    },
  };
}
export const identificationWriter = createIdentificationWriter(
  (id, enabled) => meetingsApi.setVoiceIdentification(id, enabled),
  (detail) => window.dispatchEvent(new CustomEvent('poneva:voice-identification', { detail })),
);
if (typeof window !== 'undefined')
  window.addEventListener('pagehide', () => identificationWriter.clear());
